import type { GraphQLError } from 'graphql';

import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import ContentPrerequisitesQuery, {
	type ContentPrerequisitesQuery$data,
	type ContentPrerequisitesQuery$variables,
} from './__generated__/ContentPrerequisitesQuery.graphql.ts';
import ContentRedirectQuery, {
	type ContentRedirectQuery$data,
	type ContentRedirectQuery$variables,
} from './__generated__/ContentRedirectQuery.graphql.ts';

const preloadContentPrerequisitesQuery = (
	contentId: string,
	spaceKey: string | null | undefined,
	includeDraftContentStatus?: boolean,
) =>
	query<ContentPrerequisitesQuery$data, ContentPrerequisitesQuery$variables>({
		query: ContentPrerequisitesQuery,
		errorPolicy: 'all',
		variables: {
			contentId,
			missingSpaceKey: !spaceKey,
			spaceKey,
			...(includeDraftContentStatus ? { status: ['current', 'draft'] } : {}),
		},
	});

const preloadContentRedirectQuery = ({
	contentId,
	versionOverride,
}: PreloadContentRedirectQueryArgs) =>
	query<ContentRedirectQuery$data, ContentRedirectQuery$variables>({
		query: ContentRedirectQuery,
		errorPolicy: 'all',
		variables: {
			contentId,
			versionOverride,
		},
	});

type PreloadContentRedirectQueryArgs = {
	contentId: string;
} & Pick<PreloadContentPrerequisitesNamedArgs, 'versionOverride'>;

export const preloadContentPrerequisites = (
	contentId: string,
	spaceKey: string | null | undefined,
	// Note: Add new function parameters/arguments to namedArgs below please! More than 3-4 unnamed function arguments makes it noticeably harder to comprehend the function calls.
	namedArgs?: PreloadContentPrerequisitesNamedArgs,
): Promise<
	| void
	| Error[]
	| (
			| {
					data: ContentPrerequisitesQuery$data | null;
					errors?: ReadonlyArray<GraphQLError>;
					hasErrors: boolean;
			  }
			| {
					data: ContentRedirectQuery$data | null;
					errors?: ReadonlyArray<GraphQLError>;
					hasErrors: boolean;
			  }
	  )[]
> => {
	return waitForAllReturnWhenError([
		preloadContentPrerequisitesQuery(contentId, spaceKey, namedArgs?.includeDraftContentStatus),
		preloadContentRedirectQuery({ contentId, ...namedArgs }),
	]);
};

type PreloadContentPrerequisitesNamedArgs = {
	versionOverride?: number | null;
	// When true, drafts are included in the prerequisites query status filter so the
	// preloaded data matches what routes rendering drafts (e.g. attachments page) expect.
	includeDraftContentStatus?: boolean;
};
